import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { v2ArticlesApi, publicSamplesApi } from '../../../api';
import {
  Steps,
  Card,
  Button,
  Breadcrumb,
  Typography,
  Space,
  Modal,
  message,
  Row,
  Col,
  Divider,
} from 'antd';
import {
  ExclamationCircleOutlined,
  ExportOutlined,
  FireOutlined,
  RocketOutlined,
} from '@ant-design/icons';
import RegisterPromptModal from '../../Demo/RegisterPromptModal';
import GuestBottomBar from '../../Demo/GuestBottomBar';
import Step1BasicDesign from './step1/Step1BasicDesign';
import Step2BaseOutline from './step2/Step2BaseOutline';
import GenerationOutlinePage from './step2/OutlinePage';
import Step3Composer from './step3/Step3Composer';
import Step4Container from './step4/Step4Container';
import RealtimeProgressUI from '../Analysis/components/RealtimeProgressUI';
import { TourHelpButton } from '../../Common/EnhancedTour';

const { Step } = Steps;
const { Text } = Typography;

const AioGenerationWizard = () => {
  const navigate = useNavigate();
  const params = useParams();
  const location = useLocation();
  const hasUuid = useMemo(() => Boolean(params.uuid), [params.uuid]);

  const [isGuest, setIsGuest] = useState(!localStorage.getItem('token'));
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [sampleArticleData, setSampleArticleData] = useState(null);

  // 記事データ（current_stepを取得するため）
  const [articleData, setArticleData] = useState(null);

  // URLパスから初期ステップを決定
  const currentStep = useMemo(() => {
    if (!params.uuid) return 0; // /new の場合はSTEP1
    const path = location.pathname;
    // step4のサブステップも含めてSTEP4として扱う
    if (path.includes('/step4')) return 3;
    if (path.includes('/step3')) return 2;
    if (path.includes('/step2')) return 1;
    if (path.includes('/step1')) return 0;
    return 0; // デフォルトはSTEP1
  }, [params.uuid, location.pathname]);

  // ツアー再表示をトリガーするカスタムイベント発火
  const handleRestartTour = () => {
    // 各ステップコンポーネントがこのイベントをリッスンしてツアーを再表示
    window.dispatchEvent(new CustomEvent('restartGenerationTour'));
  };
  const [wizardState, setWizardState] = useState({
    // STEP1 で確定する情報
    selectedAnalysisId: null,
    keyword: '',
    selectedStyleId: null,
    selectedToppingIds: [],
    contentPolicyLevel: 50,
    coverageApproachLevel: 50,
    customNotes: '',
    // Base Outline
    selectedOutlineIndex: null,
    selectedOutline: null,
  });

  // STEP4のプログレス状態（パンくず上にプログレスバーを表示するため）
  const [step4Progress, setStep4Progress] = useState({
    isGenerating: false,
    progress: 0,
    currentHeading: '',
  });

  // 記事データを読み込み（current_stepを取得）
  useEffect(() => {
    if (!params.uuid) return;

    const loadArticleData = async () => {
      let article = null;
      try {
        if (isGuest) {
          // ゲスト時は public API（同じレスポンス形式）を試行
          article = await publicSamplesApi.getArticle(params.uuid);
        } else {
          article = await v2ArticlesApi.getArticle(params.uuid);
        }
      } catch (error) {
        console.error('[AioGenerationWizard] Failed to load article:', error);
        if (!isGuest) {
          // 認証ユーザーだが取得失敗
          return;
        }
        return;
      }

      if (!article) return;
      setArticleData(article);

      const path = location.pathname;
      const hasStepInUrl = path.includes('/step');
      if (!hasStepInUrl && article?.current_step) {
        const currentStepStr = String(article.current_step);
        const articleStatus = String(article.status || '').toLowerCase();
        let targetStep = 'step1';
        
        if (currentStepStr.includes('4') || articleStatus === 'completed' || articleStatus === 'step4_completed') {
          targetStep = 'step4';
        } else if (currentStepStr.includes('3') || currentStepStr === 'step3') {
          targetStep = 'step3';
        } else if (currentStepStr.includes('2') || currentStepStr === 'step2') {
          targetStep = 'step2';
        } else if (
          (currentStepStr === 'step1' || currentStepStr === '1') &&
          (articleStatus === 'draft' || articleStatus === 'generating')
        ) {
          targetStep = 'step2';
        }
        
        const newPath = `/aio/generation/${params.uuid}/${targetStep}`;
        navigate(newPath, { replace: true });
      }
    };

    loadArticleData();
  }, [params.uuid, location.pathname, navigate]);

  const handleStep1Completed = async values => {
    setWizardState(prev => ({ ...prev, ...values }));

    try {
      // スタイル設定を取得
      let selectedStyleConfig = null;
      try {
        const { v2StylesApi } = await import('../../../api');
        const stylesRes = await v2StylesApi.getStyles({
          params: { limit: 200 },
        });
        const styleList = stylesRes?.items || stylesRes || [];
        const chosen = (styleList || []).find(
          s => String(s.id) === String(values.selectedStyleId),
        );
        selectedStyleConfig = chosen?.style_config || null;

        if (selectedStyleConfig?.headings_style) {
          const allowedLevels =
            selectedStyleConfig.headings_style.allowed_levels;
          if (!allowedLevels || allowedLevels.length === 0) {
            selectedStyleConfig.headings_style.allowed_levels = ['h2', 'h3'];
          }
        }
      } catch (err) {
        console.error('❌ [STEP1→2] Failed to load style config:', err);
        selectedStyleConfig = null;
      }

      // 🔥 直接記事作成（クレジット確認はStep1BasicDesign.jsで完了済み）
      const analysisId = values.selectedAnalysisId || null;

      const payload = {
        keyword: values.keyword,
        status: 'draft',
        current_step: 'step1',
        progress: 10,
        step1_analysis_id: analysisId,
        generation_config: {
          policy: {},
          style_id: values.selectedStyleId || null,
          style_overrides: values.style_overrides || null,
          article_purpose: values.articlePurpose || null,
          custom_notes: values.customNotes || null,
        },
        prompt_variables: {
          keyword: values.keyword,
          styleConfig: selectedStyleConfig
            ? {
                headings_style: {
                  allowed_levels:
                    selectedStyleConfig?.headings_style?.allowed_levels || [],
                  counts: {
                    h2_per_article: {
                      typical:
                        selectedStyleConfig?.headings_style?.counts
                          ?.h2_per_article?.typical || null,
                    },
                    h3_per_h2: {
                      typical:
                        selectedStyleConfig?.headings_style?.counts?.h3_per_h2
                          ?.typical || null,
                    },
                  },
                  length_policy: {
                    h2_chars: {
                      typical:
                        selectedStyleConfig?.headings_style?.length_policy
                          ?.h2_chars?.typical || null,
                    },
                    h3_chars: {
                      typical:
                        selectedStyleConfig?.headings_style?.length_policy
                          ?.h3_chars?.typical || null,
                    },
                    h2_body_chars: {
                      typical:
                        selectedStyleConfig?.headings_style?.length_policy
                          ?.h2_body_chars?.typical || null,
                    },
                    h3_body_chars: {
                      typical:
                        selectedStyleConfig?.headings_style?.length_policy
                          ?.h3_body_chars?.typical || null,
                    },
                  },
                  temperature: {
                    allow_interrogative:
                      selectedStyleConfig?.headings_style?.temperature
                        ?.allow_interrogative ?? null,
                    allow_symbols:
                      selectedStyleConfig?.headings_style?.temperature
                        ?.allow_symbols || null,
                  },
                },
                prose_style: {
                  tone: {
                    intensity:
                      selectedStyleConfig?.prose_style?.tone?.intensity ?? null,
                    expertise:
                      selectedStyleConfig?.prose_style?.tone?.expertise ?? null,
                  },
                  rhythm: {
                    info_density:
                      selectedStyleConfig?.prose_style?.rhythm?.info_density ??
                      null,
                  },
                },
              }
            : null,
          searchIntentAnalysis: values.searchIntentAnalysis || null,
          topicMap: values.topicMap || null,
          competitorHeadings: values.competitorHeadings || [],
          strategyOverview: values.strategyOverview || null,
        },
      };

      const res = await v2ArticlesApi.createArticle(payload);
      const uuid = res?.uuid;

      if (uuid) {
        message.success('ベース30クレジットを消費し、生成を開始しました');
        navigate(`/aio/generation/${uuid}/step2`);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
    } catch (error) {
      console.error('❌ [STEP1→2] Failed to create article:', error);
      message.error('記事の作成に失敗しました');
    }
  };
  const handleStep1ValuesChange = (changedValues, allValues) => {
    setWizardState(prev => ({ ...prev, ...allValues }));
  };

  const handleOutlineSelected = ({ index, outline, freeText }) => {
    setWizardState(prev => ({
      ...prev,
      selectedOutlineIndex: typeof index === 'number' ? index : null,
      selectedOutline:
        outline ||
        (freeText ? { title: '自由入力', sections: freeText } : null),
    }));
    // URLを更新してSTEP3に遷移
    if (hasUuid) {
      navigate(`/aio/generation/${params.uuid}/step3`);
    }
    try {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (_) {
      window.scrollTo(0, 0);
    }
  };

  const handleComposerCompleted = nodes => {
    setWizardState(prev => ({ ...prev, composerNodes: nodes }));
    // URLを更新してSTEP4.1に遷移
    if (hasUuid) {
      navigate(`/aio/generation/${params.uuid}/step4-1`);
    }
    try {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (_) {
      window.scrollTo(0, 0);
    }
  };

  // STEPSクリック時のハンドラー
  const handleStepChange = targetStep => {
    // ⭐ 閲覧専用モード（status='completed'）の場合は自由に移動可能、DB更新なし
    if (articleData?.status === 'completed') {
      // STEP1への移動は禁止
      if (targetStep === 0) {
        message.warning('基本設計には戻ることができません');
        return;
      }

      // URLのみ変更、current_stepは更新しない
      const stepPaths = ['step1', 'step2', 'step3', 'step4'];
      const targetPath = stepPaths[targetStep];
      if (hasUuid && targetPath) {
        navigate(`/aio/generation/${params.uuid}/${targetPath}`);
        try {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        } catch (_) {
          window.scrollTo(0, 0);
        }
      }
      return;
    }

    // 通常モード：既存の進行制御ロジック
    // STEP4からSTEP1/2への移動は禁止
    if (currentStep === 3 && (targetStep === 0 || targetStep === 1)) {
      message.warning('本文生成開始後はSTEP1・STEP2に戻ることはできません');
      return;
    }

    // STEP2/STEP3からSTEP1への移動は禁止
    if ((currentStep === 1 || currentStep === 2) && targetStep === 0) {
      message.warning('構成案生成後は基本設計に戻ることはできません');
      return;
    }

    // STEP4 → STEP3 への移動時
    // current_stepが4.2以降の場合は禁止
    if (currentStep === 3 && targetStep === 2 && hasUuid) {
      const dbCurrentStep = articleData?.current_step;

      // articleDataがまだ読み込まれていない場合はURLから判定
      if (!articleData) {
        const path = location.pathname;
        const isStep4_1Path =
          path.includes('/step4-1') || path.endsWith('/step4');
        if (!isStep4_1Path) {
          message.warning('本文生成開始後は構成編集に戻ることはできません');
          return;
        }
      } else {
        // current_stepの様々な形式に対応
        const stepStr = String(dbCurrentStep || '').toLowerCase();
        const isStep4_1 =
          stepStr === '4.1' ||
          stepStr === 'step4' ||
          stepStr === 'step4.1' ||
          stepStr === '4_1' ||
          stepStr === 'step4_1' ||
          stepStr === 'step4_writing_plan_completed'; // 執筆計画完了状態

        if (!isStep4_1) {
          message.warning('本文生成開始後は構成編集に戻ることはできません');
          return;
        }
      }

      Modal.confirm({
        title: '構成編集に戻りますか？',
        icon: <ExclamationCircleOutlined />,
        content: '現在の執筆プランは消えてしまいます。よろしいですか？',
        okText: 'はい',
        cancelText: 'キャンセル',
        onOk: async () => {
          try {
            // step4_statesをクリア
            await v2ArticlesApi.updateArticle(params.uuid, {
              current_step: 'step3',
              step4_states: null,
            });
            navigate(`/aio/generation/${params.uuid}/step3`);
            message.success('構成編集に戻りました');
            window.location.hash = '';
            try {
              window.scrollTo({ top: 0, behavior: 'smooth' });
            } catch (_) {
              window.scrollTo(0, 0);
            }
          } catch (error) {
            console.error('Failed to reset to step3:', error);
            message.error('戻る処理に失敗しました');
          }
        },
      });
      return;
    }

    // STEP3 → STEP2 への移動時は確認
    if (currentStep === 2 && targetStep === 1 && hasUuid) {
      Modal.confirm({
        title: 'ベースアウトライン選択に戻りますか？',
        icon: <ExclamationCircleOutlined />,
        content: '現在編集中の構成案は消えてしまいます。よろしいですか？',
        okText: 'はい',
        cancelText: 'キャンセル',
        onOk: async () => {
          try {
            // working_draftをクリアし、ステータスをSTEP2に戻す
            await v2ArticlesApi.resetToCandidates(params.uuid);
            navigate(`/aio/generation/${params.uuid}/step2`);
            message.success('ベースアウトライン選択に戻りました');
            // ハッシュをクリア
            window.location.hash = '';
            try {
              window.scrollTo({ top: 0, behavior: 'smooth' });
            } catch (_) {
              window.scrollTo(0, 0);
            }
          } catch (error) {
            console.error('Failed to reset to candidates:', error);
            message.error('戻る処理に失敗しました');
          }
        },
      });
      return;
    } else if (targetStep < currentStep) {
      // 前のステップに戻る（通常の動作）
      // URLを更新（navigateのみ、currentStepはURLから自動判定される）
      if (hasUuid) {
        const stepPath =
          targetStep === 3
            ? 'step4-1' // デフォルトは執筆プラン
            : targetStep === 2
              ? 'step3'
              : targetStep === 1
                ? 'step2'
                : 'step1';
        navigate(`/aio/generation/${params.uuid}/${stepPath}`);
      }
      try {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } catch (_) {
        window.scrollTo(0, 0);
      }
    }
    // 先に進むことは許可しない（currentStep <= targetStepの場合は無視）
  };

  // keywordとanalysis_idをAPIから取得（STEP4のみ、初回のみ）
  useEffect(() => {
    if (!hasUuid || currentStep !== 3) return;

    let mounted = true;
    const loadMetadata = async () => {
      try {
        const apiToUse = isGuest ? publicSamplesApi : v2ArticlesApi;
        const res = await apiToUse.getArticle(params.uuid);
        if (!mounted) return;

        setWizardState(prev => ({
          ...prev,
          keyword: res?.keyword || prev.keyword,
          selectedAnalysisId: res?.analysis_id || prev.selectedAnalysisId,
        }));
      } catch (error) {
        console.error('[AioGenerationWizard] Failed to load metadata:', error);
      }
    };
    loadMetadata();
    return () => {
      mounted = false;
    };
  }, [hasUuid, params.uuid, currentStep]);

  // パンくずの文言をステップごとに変更
  const getBreadcrumbText = () => {
    const breadcrumbTexts = [
      '基本設計',
      'ベースアウトライン',
      '構成編集',
      '本文生成',
    ];
    return breadcrumbTexts[currentStep] || '基本設計';
  };

  return (
    <div>
      {/* ゲスト用サンプル説明バナー（生成結果閲覧時のみ） */}
      {isGuest && hasUuid && (
        <div style={{
          background: '#e6f4ff',
          border: '1px solid #91caff',
          borderRadius: 8,
          padding: '10px 16px',
          marginBottom: 12,
          fontSize: 13,
          color: '#0958d9',
        }}>
          生成が完了した画面です。閲覧モードとして各ステップを振り返れる状態です。
        </div>
      )}
      {/* STEP4のプログレスバー（固定表示） */}
      {currentStep === 3 &&
        step4Progress.isGenerating &&
        step4Progress.progress < 100 && (
          <RealtimeProgressUI
            currentStepMessage={step4Progress.currentHeading}
            isVisible={true}
            progress={step4Progress.progress}
            showProgress={true}
          />
        )}

      <Space
        direction="vertical"
        size={4}
        style={{ width: '100%', marginBottom: 8 }}
      >
        <div className="generation-header-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginTop: 16, flexWrap: 'wrap', gap: 12 }}>
          <Breadcrumb>
            <Breadcrumb.Item>
              <a href="/aio/dashboard">AIOコンテンツモード</a>
            </Breadcrumb.Item>
            <Breadcrumb.Item>コンテンツ生成</Breadcrumb.Item>
            <Breadcrumb.Item>{getBreadcrumbText()}</Breadcrumb.Item>
          </Breadcrumb>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {currentStep >= 1 && articleData && (
              <div
                className="generation-info-block"
                style={{
                  backgroundColor: '#fafafa',
                  padding: '6px 12px',
                  borderRadius: 6,
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '8px 16px',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                  }}
                >
                  <Typography.Text
                    style={{ fontSize: 11, color: '#999' }}
                  >
                    KW:
                  </Typography.Text>
                  <Typography.Text
                    strong
                    style={{ fontSize: 12 }}
                  >
                    {articleData.keyword || '-'}
                  </Typography.Text>
                </div>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                  }}
                >
                  <Typography.Text
                    style={{ fontSize: 11, color: '#999' }}
                  >
                    分析:
                  </Typography.Text>
                  {articleData.step1_analysis_id ? (
                    <Button
                      type="link"
                      size="small"
                      icon={<ExportOutlined />}
                      onClick={() => {
                        window.open(
                          `/aio/analysis/${articleData.step1_analysis_id}`,
                          '_blank',
                        );
                      }}
                      style={{
                        padding: 0,
                        height: 'auto',
                        fontSize: 12,
                      }}
                    >
                      あり
                    </Button>
                  ) : (
                    <Typography.Text style={{ fontSize: 12 }}>
                      なし
                    </Typography.Text>
                  )}
                </div>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                  }}
                >
                  <Typography.Text
                    style={{ fontSize: 11, color: '#999' }}
                  >
                    スタイル:
                  </Typography.Text>
                  <Typography.Text
                    strong
                    style={{ fontSize: 12 }}
                  >
                    {articleData.style?.name || '-'}
                  </Typography.Text>
                </div>
              </div>
            )}
            {!isGuest && (
              <TourHelpButton
                onClick={handleRestartTour}
                tooltip="ガイドツアーを再表示"
              />
            )}
          </div>
        </div>
        <Typography.Title
          level={1}
          className="gradient-text"
          style={{ fontSize: 24, margin: 0 }}
        >
          コンテンツ新規生成
        </Typography.Title>
        {null}
      </Space>
      <div style={{ marginTop: 24, marginBottom: 40 }}>
        <Steps
          current={currentStep}
          size="small"
          responsive
          className="v2-steps"
          onChange={targetStep => {
            // ⭐ 閲覧専用モード（status='completed'）の場合はチェックをスキップ
            const isCompletedMode = articleData?.status === 'completed';

            // STEP4.2以降の場合、STEP3（構成編集）へのクリックを無効化（通常モードのみ）
            if (!isCompletedMode && currentStep === 3 && targetStep === 2) {
              const dbCurrentStep = articleData?.current_step;

              // articleDataがまだ読み込まれていない場合はURLから判定
              if (!articleData) {
                const path = location.pathname;
                const isStep4_1Path =
                  path.includes('/step4-1') || path.endsWith('/step4');
                if (!isStep4_1Path) {
                  return; // イベントを無視
                }
              } else {
                // current_stepの様々な形式に対応
                const stepStr = String(dbCurrentStep || '').toLowerCase();
                const isStep4_1 =
                  stepStr === '4.1' ||
                  stepStr === 'step4' ||
                  stepStr === 'step4.1' ||
                  stepStr === '4_1' ||
                  stepStr === 'step4_1' ||
                  stepStr === 'step4_writing_plan_completed'; // 執筆計画完了状態
                if (!isStep4_1) {
                  return; // イベントを無視
                }
              }
            }
            handleStepChange(targetStep);
          }}
          itemRender={(item, stepProps) => {
            const idx = stepProps.index;
            const isDone = idx < currentStep;
            const isCurrent = idx === currentStep;
            const bg = isCurrent ? '#1677ff' : '#999';
            const fg = '#fff';

            // クリック可能性の判定
            let isClickable = false;

            // ⭐ 閲覧専用モード（status='completed'）の場合は全ステップクリック可能（STEP1除く）
            const isCompletedMode = articleData?.status === 'completed';
            if (isCompletedMode) {
              isClickable = idx !== 0; // STEP1以外はクリック可能
            } else if (currentStep === 3) {
              // STEP4にいる場合
              // current_stepが4.1の場合のみ、STEP3（構成編集）をクリック可能
              // 4.2以降はSTEP3もクリック不可
              const dbCurrentStep = articleData?.current_step;

              // articleDataがまだ読み込まれていない場合はデフォルトでクリック可能にする
              // （STEP3から4.1に遷移直後はarticleDataがnullの可能性があるため）
              if (!articleData) {
                // URLからサブステップを判定
                const path = location.pathname;
                const isStep4_1Path =
                  path.includes('/step4-1') || path.endsWith('/step4');
                isClickable = idx === 2 && isStep4_1Path;
              } else {
                // current_stepの様々な形式に対応
                const stepStr = String(dbCurrentStep || '').toLowerCase();
                const isStep4_1 =
                  stepStr === '4.1' ||
                  stepStr === 'step4' ||
                  stepStr === 'step4.1' ||
                  stepStr === '4_1' ||
                  stepStr === 'step4_1' ||
                  stepStr === 'step4_writing_plan_completed'; // 執筆計画完了状態
                isClickable = idx === 2 && isStep4_1;
              }
            } else if (currentStep === 2) {
              isClickable = idx === 1 || idx === 3; // STEP3にいる場合、STEP2とSTEP4がクリック可能
            } else if (currentStep === 1) {
              isClickable = idx === 2 || idx === 3; // STEP2にいる場合、STEP3とSTEP4がクリック可能
            } else {
              isClickable = idx < currentStep; // 通常は過去のステップのみクリック可能
            }

            return (
              <span
                className="v2-step-index"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 20,
                  height: 20,
                  borderRadius: '50%',
                  background: isCompletedMode
                    ? '#52c41a'
                    : isCurrent
                      ? '#1677ff'
                      : '#999', // 完了時は緑色
                  color: fg,
                  fontSize: 12,
                  lineHeight: '20px',
                  cursor: isCompletedMode
                    ? idx !== 0
                      ? 'pointer'
                      : 'default' // 閲覧モード: STEP1以外はpointer
                    : isCurrent || isDone
                      ? 'default'
                      : 'not-allowed', // 通常モード: 現在と過去はdefault、未来はnot-allowed
                  opacity: isClickable || isCurrent ? 1 : 0.7,
                }}
              >
                {isCompletedMode ? '✓' : isDone ? '✓' : idx}
              </span>
            );
          }}
        >
          {[0, 1, 2, 3].map(idx => {
            // ⭐ 閲覧専用モード（status='completed'）の場合は全ステップ有効
            if (articleData?.status === 'completed') {
              return (
                <Steps.Step
                  key={idx}
                  title={['基本設計', 'ベース選択', '構成編集', '本文'][idx]}
                  style={{ cursor: idx === 0 ? 'not-allowed' : 'pointer' }} // STEP1はカーソル禁止
                />
              );
            }

            // STEP2/STEP3にいる場合、STEP1は無効化
            // STEP4にいる場合、STEP1/STEP2は無効化
            // STEP4.2以降にいる場合、STEP3も無効化
            let isDisabled = false;

            if ((currentStep === 1 || currentStep === 2) && idx === 0) {
              // STEP2/STEP3にいる場合、STEP1は無効化
              isDisabled = true;
            } else if (currentStep === 3 && (idx === 0 || idx === 1)) {
              // STEP4にいる場合、STEP1/STEP2は無効化
              isDisabled = true;
            } else if (currentStep === 3 && idx === 2) {
              // STEP4にいる場合、STEP3は条件付きで無効化
              const dbCurrentStep = articleData?.current_step;

              // articleDataがまだ読み込まれていない場合はURLから判定
              if (!articleData) {
                const path = location.pathname;
                const isStep4_1Path =
                  path.includes('/step4-1') || path.endsWith('/step4');
                isDisabled = !isStep4_1Path; // 4.2以降は無効化
              } else {
                // current_stepの様々な形式に対応
                const stepStr = String(dbCurrentStep || '').toLowerCase();
                const isStep4_1 =
                  stepStr === '4.1' ||
                  stepStr === 'step4' ||
                  stepStr === 'step4.1' ||
                  stepStr === '4_1' ||
                  stepStr === 'step4_1' ||
                  stepStr === 'step4_writing_plan_completed'; // 執筆計画完了状態
                isDisabled = !isStep4_1; // 4.2以降は無効化
              }
            }

            return (
              <Step
                key={idx}
                title={
                  ['基本設計', 'ベースアウトライン', '構成編集', '本文生成'][
                    idx
                  ]
                }
                icon={null}
                className={isDisabled ? 'step-disabled' : ''}
                style={{
                  cursor: isDisabled
                    ? 'not-allowed'
                    : idx < currentStep ||
                        (currentStep === 1 && (idx === 2 || idx === 3)) ||
                        (currentStep === 2 && (idx === 1 || idx === 3))
                      ? 'pointer'
                      : 'default',
                }}
              />
            );
          })}
        </Steps>
      </div>

      {currentStep === 0 && (
        <Step1BasicDesign
          initialValues={wizardState}
          onComplete={isGuest && !hasUuid ? () => setShowRegisterModal(true) : handleStep1Completed}
          onValuesChange={handleStep1ValuesChange}
          isGuest={isGuest}
          onGuestAction={() => setShowRegisterModal(true)}
        />
      )}

      {currentStep === 1 &&
        (hasUuid ? (
          <GenerationOutlinePage articleData={articleData} isGuest={isGuest} />
        ) : (
          <Step2BaseOutline
            keyword={wizardState.keyword}
            onSelectOutline={handleOutlineSelected}
          />
        ))}

      {currentStep === 2 && (
        <Step3Composer
          initialOutline={wizardState.selectedOutline}
          keyword={wizardState.keyword}
          analysisId={wizardState.selectedAnalysisId}
          uuid={params.uuid}
          onComplete={handleComposerCompleted}
          articleData={articleData}
          isGuest={isGuest}
        />
      )}

      {currentStep === 3 && (
        <Step4Container
          key={params.uuid || 'step4-default'}
          keyword={wizardState.keyword}
          onProgressUpdate={progressData => {
            setStep4Progress(progressData);
          }}
          onComplete={() => {
            message.success('記事が完成しました！');
          }}
          articleData={articleData}
          isGuest={isGuest}
        />
      )}

      {isGuest && (
        <RegisterPromptModal
          open={showRegisterModal}
          onClose={() => setShowRegisterModal(false)}
          triggerAction="コンテンツ生成"
        />
      )}

      {isGuest && hasUuid && <GuestBottomBar />}
    </div>
  );
};

export default AioGenerationWizard;
